// LearnCore Frontend - 创建文言文材料页面

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import TagEditor, { clearTagCache } from '../components/TagEditor';
import type { CreateClassicalPassageRequest } from '../types';
import {
  BookOpenIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

export default function CreateClassicalChinesePage() {
  const navigate = useNavigate();

  // 基础信息
  const [title, setTitle] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [autoExtract, setAutoExtract] = useState(true);

  // 标签状态
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  // UI状态
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missingTags, setMissingTags] = useState<string[]>([]);
  const [creatingTags, setCreatingTags] = useState<Set<string>>(new Set());

  // 保存文言文材料
  const handleSave = async () => {
    if (!title.trim()) {
      setError('请输入标题');
      return;
    }

    if (!originalText.trim()) {
      setError('请输入原文');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const passageData: CreateClassicalPassageRequest = {
        title: title.trim(),
        original_text: originalText.trim(),
        is_public: isPublic,
        auto_extract: autoExtract,
        tag_ids: selectedTagIds,
      };

      const response = await apiClient.createClassicalPassage(passageData);

      if (response.passage) {
        // 如果有标签，应用标签
        if (selectedTagIds.length > 0 && response.passage.id) {
          try {
            await apiClient.applyTagsToClassicalPassage(response.passage.id, selectedTagIds);
          } catch (tagErr) {
            console.error('应用标签失败:', tagErr);
            // 标签失败不阻塞创建流程
          }
        }
        
        // 如果启用了自动提取，跳转到列表页；否则跳转到学习页
        if (autoExtract) {
          navigate('/classical-chinese?message=创建成功，正在后台提取内容，请稍后查看');
        } else {
          navigate(`/classical-chinese/${response.passage.id}`);
        }
      }

    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.error_type === 'missing_tags' && errorData?.missing_tags) {
        // 显示标签缺失错误，提供一键创建功能
        setMissingTags(errorData.missing_tags);
        setError(null);
      } else {
        setError(errorData?.error || '保存失败，请稍后重试');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // 创建缺失的标签
  const createMissingTag = async (tagName: string) => {
    try {
      setCreatingTags(prev => new Set(prev).add(tagName));
      await apiClient.createCustomTag(tagName);
      
      // 清除自定义标签缓存，强制刷新
      clearTagCache('custom');
      
      // 从缺失标签列表中移除
      setMissingTags(prev => prev.filter(name => name !== tagName));
      
      // 重新尝试创建文言文材料
      if (missingTags.length === 1) {
        // 如果这是最后一个缺失的标签，自动重试创建
        setTimeout(() => {
          handleSave();
        }, 500);
      }
    } catch (err: any) {
      console.error('创建标签失败:', err);
      setError('创建标签失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTags(prev => {
        const newSet = new Set(prev);
        newSet.delete(tagName);
        return newSet;
      });
    }
  };

  // 创建所有缺失的标签
  const createAllMissingTags = async () => {
    try {
      setCreatingTags(new Set(missingTags));
      
      // 批量创建标签
      const promises = missingTags.map(tagName => apiClient.createCustomTag(tagName));
      await Promise.all(promises);
      
      // 清除自定义标签缓存，强制刷新
      clearTagCache('custom');
      
      // 清空缺失标签列表
      setMissingTags([]);
      
      // 自动重试创建文言文材料
      setTimeout(() => {
        handleSave();
      }, 500);
    } catch (err: any) {
      console.error('批量创建标签失败:', err);
      setError('批量创建标签失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTags(new Set());
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
          <BookOpenIcon className="h-8 w-8 mr-3 text-amber-600" />
          创建文言文材料
        </h1>
        <p className="mt-2 text-gray-600">
          输入标题和文言文原文，选择是否自动提取学习内容
        </p>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">操作失败</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={() => setError(null)}
                  className="btn-outline text-sm"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 缺失标签提示 */}
      {missingTags.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                以下标签不存在，请先创建：
              </h3>
              <div className="mt-2">
                <div className="flex flex-wrap gap-2 mb-3">
                  {missingTags.map(tagName => (
                    <button
                      key={tagName}
                      onClick={() => createMissingTag(tagName)}
                      disabled={creatingTags.has(tagName) || isSaving}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {creatingTags.has(tagName) ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-yellow-600" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          创建中...
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          {tagName}
                        </>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={createAllMissingTags}
                    disabled={creatingTags.size > 0 || isSaving}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {creatingTags.size > 0 ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        批量创建中...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        一键创建所有标签
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMissingTags([])}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors"
                  >
                    取消
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {/* 基本信息卡片 */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">基本信息</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              标题 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
              placeholder="例如：陋室铭"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              文言文原文 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 font-serif"
              placeholder="粘贴或输入文言文原文..."
            />
          </div>

          {/* 选项 */}
          <div className="space-y-4 pt-4 border-t">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoExtract}
                onChange={(e) => setAutoExtract(e.target.checked)}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  <SparklesIcon className="h-4 w-4 inline mr-1 text-amber-600" />
                  自动智能提取
                </p>
                <p className="text-xs text-gray-500">
                  自动提取译文、实词虚词、语法句式、文学常识等学习内容（在后台进行，稍后查看结果）
                </p>
              </div>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {isPublic ? (
                    <><EyeIcon className="h-4 w-4 inline mr-1" />公开分享</>
                  ) : (
                    <><EyeSlashIcon className="h-4 w-4 inline mr-1" />私有</>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  允许其他用户查看和学习这份材料
                </p>
              </div>
            </label>
          </div>

          {/* 标签编辑器 */}
          <div className="pt-2 border-t border-gray-200">
            <TagEditor
              initialTags={[]}
              onTagsChange={(tagIds) => setSelectedTagIds(tagIds)}
              disabled={isSaving}
            />
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => navigate('/classical-chinese')}
              className="btn-outline"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !title.trim() || !originalText.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
            >
              {isSaving ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  创建中...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  创建文言文材料
                </>
              )}
            </button>
          </div>
        </div>

        {/* 说明文字 */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-amber-900 mb-2">📜 使用提示</h3>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>• <strong>启用自动提取：</strong>创建后系统会在后台自动分析原文，提取译文、实词虚词、语法句式、文学常识等内容</li>
            <li>• <strong>不启用自动提取：</strong>创建后进入学习页面，可以点击"提取"按钮触发分析</li>
            <li>• 后台处理时间取决于原文长度，通常在1-2分钟内完成</li>
            <li>• 您可以在列表页面查看处理状态（处理中/已完成/失败）</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

